/**
 * KISSED University · Herbal Science Waitlist.
 * Route: /university/herbal-waitlist
 *
 * Quiz-driven shortcut: the College of Herbal Science programs are in
 * development. This page captures interest via a mailto link (single
 * source of truth for the waitlist contact) and offers paths back to the
 * landing/quiz.
 */
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Leaf, Mail } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { clearQuizState } from "./quizData";

export default function UniversityHerbalWaitlist() {
  const navigate = useNavigate();
  const mailto = "mailto:info@kissedbycvon.com?subject=Herbal%20Science%20Waitlist%20Interest";

  return (
    <div data-testid="university-herbal-waitlist" className="min-h-screen bg-gradient-to-b from-emerald-50/40 via-[#faf7f5] to-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/university" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">College of Herbal Science</span>
          </Link>
        </div>
      </header>

      <section className="border-b border-slate-200 px-6 py-14 text-center">
        <div className="max-w-2xl mx-auto">
          <Link to="/university" className="text-xs text-slate-600 inline-flex items-center hover:text-emerald-800 mb-6">
            <ChevronLeft className="w-3 h-3 mr-1" /> Back
          </Link>
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-white px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-emerald-800 font-semibold mb-4">
            <Leaf className="w-3 h-3" /> Coming Soon
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-slate-900 leading-tight" data-testid="herbal-title">
            Herbal Science Pathways
          </h1>
          <p className="mt-4 text-base text-slate-700 max-w-xl mx-auto leading-relaxed">
            Our Herbal Science programs are in development. Four levels are planned — from Certified Herbalist through Doctor of Herbal Science.
          </p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50/50 p-6 mb-6" data-testid="herbal-levels">
          <h2 className="font-display text-lg text-emerald-900 mb-3">Planned Levels</h2>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><strong className="text-slate-900">Level I</strong> — Certified Herbalist (300 Hours)</li>
            <li><strong className="text-slate-900">Level II</strong> — Advanced Herbal Practitioner (600 Hours)</li>
            <li><strong className="text-slate-900">Level III</strong> — Master Herbal Medicine Practitioner (900 Hours)</li>
            <li><strong className="text-slate-900">Level IV</strong> — Doctor of Herbal Science (1,200 Hours)</li>
          </ul>
        </div>

        <p className="text-sm text-slate-600 text-center mb-4">
          Interested in Herbal Science? Contact our admissions team to be added to the waitlist.
        </p>
        <div className="space-y-3">
          <a href={mailto} data-testid="herbal-waitlist-btn">
            <Button size="lg" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full">
              <Mail className="w-4 h-4 mr-2" /> Join the Herbal Science Waitlist
            </Button>
          </a>
          <Button
            variant="outline"
            className="w-full rounded-full"
            onClick={() => { clearQuizState(); navigate("/university/quiz"); }}
            data-testid="herbal-retake-btn"
          >
            Retake the Pathway Quiz
          </Button>
          <Link to="/university#programs-of-study" data-testid="herbal-browse-btn">
            <Button variant="ghost" className="w-full text-slate-600">
              Explore Trichology &amp; CE Pathways
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
